'use client';

import '../calendar-new-layout.scss';

import React from 'react';
import { usePathname } from 'next/navigation';
import type { Breadcrumb } from '@benzinga/content-manager';
import { HeroPageHeader, type HeroPageHeaderTab } from '../../../_components/LayoutPageHeader/HeroPageHeader';
import UpdatedBadge from '../../reports/components/UpdatedBadge';
import { usePreservedQueryPath } from '../usePreservedQueryPath';

export interface CalendarNewLayoutHeaderNavLink {
  href: string;
  label: string;
}

export interface CalendarNewLayoutHeaderProps {
  breadcrumbs: Breadcrumb[];
  title: string;
  subtitle?: string;
  description?: string;
  dateTimestamp?: number | null;
  canonical: string;
  showEdgeBadge: boolean;
  basePathOverride?: string;
  showTradingIdeasTab?: boolean;
  presentedByImage?: string;
  navLinks?: CalendarNewLayoutHeaderNavLink[];
  navAriaLabel?: string;
}

export function CalendarNewLayoutHeader({
  breadcrumbs,
  title,
  subtitle,
  description,
  dateTimestamp,
  canonical,
  showEdgeBadge,
  basePathOverride,
  showTradingIdeasTab = true,
  presentedByImage,
  navLinks,
  navAriaLabel = 'Page sections',
}: CalendarNewLayoutHeaderProps) {
  const pathname = usePathname();
  const withPreservedQuery = usePreservedQueryPath();
  const tabBasePath = basePathOverride && showTradingIdeasTab ? basePathOverride : undefined;
  const hasPresentedByImage = Boolean(presentedByImage?.trim());

  let tabs: HeroPageHeaderTab[] = [];
  if (tabBasePath) {
    const tradingIdeasPath = `${tabBasePath}/trading-ideas`;
    tabs = [
      {
        href: withPreservedQuery(tabBasePath),
        id: 'overview',
        isActive: pathname === tabBasePath,
        label: 'Overview',
      },
      {
        href: withPreservedQuery(tradingIdeasPath),
        id: 'trading-ideas',
        isActive: pathname === tradingIdeasPath,
        label: 'Trading Ideas',
      },
    ];
  } else if (navLinks?.length) {
    tabs = navLinks.map(link => ({
      href: link.href,
      id: link.href,
      isActive: !link.href.startsWith('#') && pathname === link.href,
      label: link.label,
    }));
  }

  return (
    <HeroPageHeader
      breadcrumbs={breadcrumbs}
      buttonLocation="calendar_page"
      canonical={canonical}
      description={description}
      leadingContent={
        hasPresentedByImage ? (
          <div className="calendar-presented-by">
            <span className="calendar-presented-by__label">Presented by</span>
            <img src={presentedByImage} alt="SoFi" className="calendar-presented-by__logo" />
          </div>
        ) : undefined
      }
      metaContent={typeof dateTimestamp === 'number' ? <UpdatedBadge date={dateTimestamp} /> : undefined}
      navAriaLabel={tabBasePath ? 'Calendar sections' : navAriaLabel}
      showEdgeBadge={showEdgeBadge}
      subtitle={subtitle}
      tabs={tabs}
      title={title}
    />
  );
}
